import { useState } from "react";
import { CheckCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useApi, StatusBadge, Loading, Empty, Select, Pager, TableWrap } from "../common";
import { useDirectory } from "../LeadForm";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { fdate } from "../../lib/format";
import { REVIEW_LABEL, ScorePill, assigneeOptions, assignPayload } from "./shared";
import ProspectDialog from "./ProspectDialog";

const REVIEW_TABS = [["pending", "Awaiting approval"], ["below_threshold", "Below threshold"], ["approved", "Approved"], ["rejected", "Rejected"], ["merged", "Merged"], ["", "All"]];

export default function ProspectQueue({ tick }) {
  const [f, setF] = useState({ review_status: "pending", q: "", qualification: "", verification_status: "", state: "", city: "", source: "", sort: "first_discovered_at" });
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [openId, setOpenId] = useState(null);
  const sales = useDirectory("sales");
  const qs = new URLSearchParams({ ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), page, page_size: 25 }).toString();
  const { data, loading, reload } = useApi(`/leadgen/prospects?${qs}`, [tick]);
  const set = (k) => (v) => { setF({ ...f, [k]: v?.target ? v.target.value : v }); setPage(1); setSel([]); };
  const selectable = (data?.items || []).filter((p) => ["pending", "below_threshold"].includes(p.review_status));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const bulk = async () => {
    try {
      const r = await api.post("/leadgen/prospects/bulk-approve", { ids: sel, ...assignPayload(assignee) });
      toast.success(`${r.data.approved.length} approved and sent to CRM leads`);
      r.data.failed.forEach((x) => toast.warning(x.error));
      setSel([]); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="leadgen-queue">
      <div className="flex flex-wrap gap-1.5 mb-3">
        {REVIEW_TABS.map(([v, l]) => (
          <button key={l} onClick={() => set("review_status")(v)} data-testid={`queue-filter-${v || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${f.review_status === v ? "vmw-gradient text-white" : "bg-muted text-slate-600 hover:bg-violet-50"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2 mb-3">
        <input data-testid="queue-search" className="vmw-input col-span-2" placeholder="Search business, phone, email, website, code…" value={f.q} onChange={set("q")} />
        <Select testid="queue-filter-qualification" placeholder="All qualifications" options={["High Priority", "Medium Priority", "Low Priority", "Needs Verification"]} value={f.qualification} onChange={set("qualification")} />
        <Select testid="queue-filter-verification" placeholder="All verification" options={["Newly Discovered", "Verified", "Needs Review", "Stale", "Invalid", "Duplicate"]} value={f.verification_status} onChange={set("verification_status")} />
        <Select testid="queue-filter-state" placeholder="All states" options={data?.states || []} value={f.state} onChange={set("state")} />
        <Select testid="queue-filter-city" placeholder="All cities" options={data?.cities || []} value={f.city} onChange={set("city")} />
        <Select testid="queue-sort" options={[{ value: "first_discovered_at", label: "Newest first" }, { value: "score", label: "Highest score" }, { value: "business_name", label: "Name A–Z" }]} value={f.sort} onChange={set("sort")} />
      </div>
      {sel.length > 0 && (
        <div className="vmw-card p-3 mb-3 flex flex-wrap items-center gap-2 bg-violet-50/60" data-testid="queue-bulk-bar">
          <span className="text-sm font-semibold">{sel.length} selected</span>
          <Select testid="queue-bulk-assignee" className="w-60" placeholder="Approve without assigning" options={assigneeOptions(sales)} value={assignee} onChange={setAssignee} />
          <Button size="sm" className="vmw-gradient" onClick={bulk} data-testid="queue-bulk-approve"><CheckCheck className="h-4 w-4 mr-1" />Approve to CRM</Button>
        </div>
      )}
      <TableWrap>
        {loading && !data ? <Loading /> : !data?.items.length ? <Empty text="No prospects match these filters" /> : (
          <table className="vmw-table" data-testid="queue-table">
            <thead><tr>
              <th><Checkbox data-testid="queue-select-all" checked={selectable.length > 0 && sel.length === selectable.length} onCheckedChange={(v) => setSel(v ? selectable.map((p) => p.id) : [])} /></th>
              <th>Business</th><th>Location</th><th>Contact</th><th>Source</th><th>Score</th><th>Qualification</th><th>Verification</th><th>Status</th><th>Discovered</th>
            </tr></thead>
            <tbody>{data.items.map((p) => (
              <tr key={p.id} className="cursor-pointer" onClick={() => setOpenId(p.id)} data-testid={`prospect-row-${p.code}`}>
                <td onClick={(e) => e.stopPropagation()}>{["pending", "below_threshold"].includes(p.review_status) && <Checkbox data-testid={`prospect-select-${p.code}`} checked={sel.includes(p.id)} onCheckedChange={() => toggle(p.id)} />}</td>
                <td><div className="font-semibold text-[#1A1A2E]">{p.business_name}</div><div className="text-[11px] text-muted-foreground">{p.code}{p.contact_name ? ` · ${p.contact_name}` : ""}</div></td>
                <td className="whitespace-nowrap">{[p.city, p.state].filter(Boolean).join(", ") || "—"}</td>
                <td className="text-xs"><div>{p.phone || "—"}</div><div className="text-muted-foreground truncate max-w-[180px]">{p.email}</div></td>
                <td className="text-xs whitespace-nowrap">{p.source}</td>
                <td><ScorePill score={p.score} /></td>
                <td><StatusBadge value={p.qualification} /></td>
                <td><StatusBadge value={p.verification_status} /></td>
                <td><StatusBadge value={REVIEW_LABEL[p.review_status]} /></td>
                <td className="text-xs whitespace-nowrap">{fdate(p.discovered_date)}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
        {data && <Pager page={page} total={data.total} pageSize={25} onPage={setPage} />}
      </TableWrap>
      <ProspectDialog id={openId} onClose={() => setOpenId(null)} onChanged={reload} sales={sales} />
    </div>
  );
}
